import { WhatsNewArticle, FilterState } from "../types/whatsNew.types";

export class WhatsNewFilterService {
  static filterArticles(
    articles: WhatsNewArticle[],
    filters: FilterState
  ): WhatsNewArticle[] {
    return articles
      .filter((article) => {
        const matchesSearch = this.matchesSearchFilter(
          article,
          filters.searchQuery
        );
        const matchesModule = this.matchesModuleFilter(
          article,
          filters.selectedModuleFilter
        );
        const matchesUpdateType = this.matchesUpdateTypeFilter(
          article,
          filters.selectedUpdateTypeFilter
        );
        const matchesDate = this.matchesDateFilter(
          article,
          filters.selectedDateFilter,
          filters.customStartDate,
          filters.customEndDate
        );
        const matchesStatus = this.matchesStatusFilter(
          article,
          filters.selectedStatusFilter
        );

        return (
          matchesSearch &&
          matchesModule &&
          matchesUpdateType &&
          matchesDate &&
          matchesStatus
        );
      })
      .sort(this.sortByDateCreated);
  }

  private static matchesSearchFilter(
    article: WhatsNewArticle,
    searchQuery: string
  ): boolean {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    return (
      article.title.toLowerCase().includes(query) ||
      (article.body ? article.body.toLowerCase().includes(query) : false)
    );
  }

  private static matchesModuleFilter(
    article: WhatsNewArticle,
    selectedModuleFilter: string[]
  ): boolean {
    if (selectedModuleFilter.includes("All")) return true;

    const activeFilters = selectedModuleFilter.filter(
      (filter) => filter !== "All"
    );

    if (Array.isArray(article.module)) {
      return activeFilters.every((filter) => article.module.includes(filter));
    } else {
      return (
        activeFilters.length === 1 &&
        selectedModuleFilter.includes(article.module)
      );
    }
  }

  private static matchesUpdateTypeFilter(
    article: WhatsNewArticle,
    selectedUpdateTypeFilter: string
  ): boolean {
    if (selectedUpdateTypeFilter === "All") return true;
    return article.featureUpdateType === selectedUpdateTypeFilter;
  }

  private static matchesDateFilter(
    article: WhatsNewArticle,
    selectedDateFilter: string,
    customStartDate: string,
    customEndDate: string
  ): boolean {
    if (!selectedDateFilter) return true;

    const articleDate = article.createdAt
      ? article.createdAt.toDate()
      : new Date(0);
    const now = new Date();
    now.setHours(23, 59, 59, 999); // End of today

    switch (selectedDateFilter) {
      case "Today": {
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        return articleDate >= startOfToday && articleDate <= now;
      }
      case "Last 7 days": {
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 7);
        sevenDaysAgo.setHours(0, 0, 0, 0);
        return articleDate >= sevenDaysAgo && articleDate <= now;
      }
      case "Last 30 days": {
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(now.getDate() - 30);
        thirtyDaysAgo.setHours(0, 0, 0, 0);
        return articleDate >= thirtyDaysAgo && articleDate <= now;
      }
      case "Custom range": {
        if (!customStartDate && !customEndDate) return true;

        const startDate = customStartDate
          ? new Date(customStartDate)
          : new Date(0);
        startDate.setHours(0, 0, 0, 0);

        const endDate = customEndDate ? new Date(customEndDate) : new Date();
        endDate.setHours(23, 59, 59, 999);

        return articleDate >= startDate && articleDate <= endDate;
      }
      default:
        return true;
    }
  }

  private static matchesStatusFilter(
    article: WhatsNewArticle,
    selectedStatusFilter: string
  ): boolean {
    if (!selectedStatusFilter) return true;
    if (selectedStatusFilter === "Active") {
      return !article.archived;
    }
    if (selectedStatusFilter === "Archive") {
      return !!article.archived;
    }
    return true;
  }

  private static sortByDateCreated(
    a: WhatsNewArticle,
    b: WhatsNewArticle
  ): number {
    const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
    const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
    return dateB.getTime() - dateA.getTime();
  }

  static clearAllFilters(): FilterState {
    return {
      searchQuery: "",
      selectedModuleFilter: ["All"],
      selectedUpdateTypeFilter: "All",
      selectedDateFilter: "",
      selectedStatusFilter: "",
      customStartDate: "",
      customEndDate: "",
    };
  }
}
